import { IconButton } from "@/design-system/IconButton/IconButton";
import { ItemScoreDiamond } from "@/design-system/ItemScore/ItemScoreDiamond";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { Pencil } from "lucide-react";
import { useState } from "react";

/**
 * "{earned} / {target}" with the item-score diamond — the live points readout.
 * With `onCommitProgress`, the pencil turns the green number into an input for
 * the item score already earned; Enter or blur commits, Escape cancels.
 * The input starts from the total on screen; it cannot go below the cards
 * already added (`min`), and a figure at the target or past it means done.
 */
export const PointsCounter = ({
  earned,
  target,
  small = false,
  min = 0,
  onCommitProgress,
  loading = false,
}: {
  earned: number;
  target: number;
  small?: boolean;
  /** The added cards' points: the least the total can be. */
  min?: number;
  onCommitProgress?: (points: number) => void;
  loading?: boolean;
}) => {
  const { t } = useTranslation("sbcs");
  const [draft, setDraft] = useState<string | null>(null);

  const commit = () => {
    if (draft === null) return;
    const next = Math.min(target, Math.max(min, Math.floor(Number(draft))));
    setDraft(null);
    if (!Number.isNaN(next) && next !== earned) onCommitProgress?.(next);
  };

  return (
    <div className="flex items-center gap-1">
      {onCommitProgress && draft === null && !loading && (
        <IconButton
          shape="round"
          icon={<Pencil size={small ? 12 : 14} className="text-neutral-400" />}
          /* Desktop: 32px, the height of the Add players button on its row. */
          className={cn(
            "mr-1 bg-transparent border-neutral-700",
            small ? "size-6" : "size-8",
          )}
          aria-label={t("streamline.editProgress")}
          title={t("streamline.editProgress")}
          onClick={() => setDraft(String(earned))}
        />
      )}
      {draft === null ? (
        <span
          className={cn(
            "font-extrabold text-streamline-success",
            small ? "text-base" : "text-lg",
          )}
        >
          {earned.toLocaleString()}
        </span>
      ) : (
        <input
          type="number"
          inputMode="numeric"
          min={min}
          max={target}
          value={draft}
          autoFocus
          onFocus={(e) => e.target.select()}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") setDraft(null);
          }}
          className={cn(
            "w-16 rounded-md border border-neutral-600 bg-neutral-800 px-1.5 font-extrabold text-streamline-success outline-none focus:border-neutral-400",
            small ? "text-base" : "text-lg",
          )}
        />
      )}
      <span
        className={cn(
          "font-bold text-neutral-500",
          small ? "text-xs" : "text-sm",
        )}
      >
        / {target.toLocaleString()}
      </span>
      <ItemScoreDiamond size={small ? 13 : 15} />
    </div>
  );
};
